import React, { useMemo, useRef, useState, useEffect } from "react";
import DOMPurify from "dompurify";

import {
  FiChevronDown,
  FiChevronUp,
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiImage,
  FiMail,
  FiMapPin,
  FiPhone,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
  FiUsers,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import AssociationStatusBadge from "./AssociationStatusBadge";

import {
  formatDateTime,
  getGoogleMapsUrl,
  normalizeExternalUrl,
} from "../utils/associationsAdminUtils";

/* -------------------------------------------------------------------------- */
/*                              Configuration                                 */
/* -------------------------------------------------------------------------- */

const DESCRIPTION_COLLAPSED_HEIGHT = 118;

/* -------------------------------------------------------------------------- */
/*                           AssociationCard                                  */
/* -------------------------------------------------------------------------- */

export default function AssociationCard({
  item,
  actionId,
  onPreview,
  onEdit,
  onToggle,
  onAskDelete,
}) {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionOverflowing, setDescriptionOverflowing] = useState(false);

  const descriptionRef = useRef(null);

  /* ------------------------------------------------------------------------ */
  /*                              Données                                     */
  /* ------------------------------------------------------------------------ */

  const logoUrl = item?.logo_asso
    ? toAbsolutePublicUrl(item.logo_asso)
    : "";

  const toggleLoading = actionId === `toggle-${item.id}`;

  const mapsUrl = getGoogleMapsUrl(item.gps_asso);

  /* ------------------------------------------------------------------------ */
  /*                         Description sécurisée                            */
  /* ------------------------------------------------------------------------ */

  const safeDescription = useMemo(() => {
    const html = String(item?.description_asso || "").trim();

    if (!html) return "";

    return DOMPurify.sanitize(html, {
      USE_PROFILES: {
        html: true,
      },
      ADD_ATTR: ["target", "rel"],
    });
  }, [item?.description_asso]);

  /* ------------------------------------------------------------------------ */
  /*                    Détection description trop longue                    */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const element = descriptionRef.current;

    if (!element || !safeDescription) {
      setDescriptionOverflowing(false);
      return;
    }

    const checkOverflow = () => {
      /*
       * On mesure la hauteur naturelle.
       * Le contenu est considéré comme long s'il dépasse
       * la hauteur prévue pour l'état replié.
       */
      const naturalHeight = element.scrollHeight;

      setDescriptionOverflowing(
        naturalHeight > DESCRIPTION_COLLAPSED_HEIGHT + 8,
      );
    };

    checkOverflow();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(checkOverflow)
        : null;

    if (resizeObserver) {
      resizeObserver.observe(element);
    }

    window.addEventListener("resize", checkOverflow);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [safeDescription]);

  /* ------------------------------------------------------------------------ */
  /*                              Render                                      */
  /* ------------------------------------------------------------------------ */

  return (
    <article className="group overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 transition hover:-translate-y-0.5 hover:border-sky-200 hover:bg-white hover:shadow-xl hover:shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-500/30 dark:hover:bg-slate-900 dark:hover:shadow-black/20">
      <div className="flex min-h-full flex-col p-4 sm:p-5">
        {/* ------------------------------------------------------------------ */}
        {/*                              En-tête                               */}
        {/* ------------------------------------------------------------------ */}

        <div className="flex gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-[1.4rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={item.nom_asso || "Logo de l'association"}
                className="h-full w-full object-contain p-2"
              />
            ) : (
              <FiImage className="text-3xl text-slate-300 dark:text-slate-600" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <AssociationStatusBadge active={!!item.is_active} />

              {item.categorie ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
                  <FiUsers />
                  {item.categorie}
                </span>
              ) : null}

              {item.gps_asso ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                  <FiMapPin />
                  GPS
                </span>
              ) : null}
            </div>

            <h3 className="mt-3 line-clamp-2 text-lg font-bold leading-snug text-slate-950 dark:text-white">
              {item.nom_asso || "Association sans nom"}
            </h3>

            {item.contact_asso ? (
              <p className="mt-1 line-clamp-1 text-sm font-bold text-slate-500 dark:text-slate-400">
                {item.contact_asso}
                {item.fonction_asso
                  ? ` — ${item.fonction_asso}`
                  : ""}
              </p>
            ) : null}
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/*                       Description enrichie                         */}
        {/* ------------------------------------------------------------------ */}

        <div className="relative mt-4">
          {safeDescription ? (
            <>
              <div
                className={[
                  "relative overflow-hidden transition-[max-height] duration-300 ease-out",
                  descriptionExpanded
                    ? "max-h-[1200px]"
                    : "max-h-[118px]",
                ].join(" ")}
              >
                <div
                  ref={descriptionRef}
                  className="
                    text-sm
                    leading-6
                    text-slate-600
                    dark:text-slate-300

                    [&_p]:mb-2
                    [&_p:last-child]:mb-0

                    [&_strong]:font-bold
                    [&_strong]:text-slate-800
                    dark:[&_strong]:text-slate-100

                    [&_b]:font-bold
                    [&_b]:text-slate-800
                    dark:[&_b]:text-slate-100

                    [&_em]:italic
                    [&_i]:italic

                    [&_u]:underline

                    [&_h1]:mb-2
                    [&_h1]:mt-3
                    [&_h1]:text-lg
                    [&_h1]:font-black
                    [&_h1]:text-slate-900
                    dark:[&_h1]:text-white

                    [&_h2]:mb-2
                    [&_h2]:mt-3
                    [&_h2]:text-base
                    [&_h2]:font-black
                    [&_h2]:text-slate-900
                    dark:[&_h2]:text-white

                    [&_h3]:mb-1
                    [&_h3]:mt-2
                    [&_h3]:font-bold
                    [&_h3]:text-slate-800
                    dark:[&_h3]:text-slate-100

                    [&_ul]:my-2
                    [&_ul]:list-disc
                    [&_ul]:space-y-1
                    [&_ul]:pl-5

                    [&_ol]:my-2
                    [&_ol]:list-decimal
                    [&_ol]:space-y-1
                    [&_ol]:pl-5

                    [&_li]:pl-0.5

                    [&_blockquote]:my-2
                    [&_blockquote]:border-l-4
                    [&_blockquote]:border-sky-300
                    [&_blockquote]:pl-3
                    [&_blockquote]:italic
                    [&_blockquote]:text-slate-500
                    dark:[&_blockquote]:border-sky-700
                    dark:[&_blockquote]:text-slate-400

                    [&_a]:font-bold
                    [&_a]:text-sky-600
                    [&_a]:underline
                    [&_a]:underline-offset-2
                    [&_a:hover]:text-sky-700
                    dark:[&_a]:text-sky-400
                    dark:[&_a:hover]:text-sky-300
                  "
                  dangerouslySetInnerHTML={{
                    __html: safeDescription,
                  }}
                />

                {!descriptionExpanded && descriptionOverflowing ? (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent group-hover:from-white group-hover:via-white/95 dark:from-slate-950 dark:via-slate-950/95 dark:group-hover:from-slate-900 dark:group-hover:via-slate-900/95" />
                ) : null}
              </div>

              {descriptionOverflowing ? (
                <button
                  type="button"
                  onClick={() =>
                    setDescriptionExpanded((previous) => !previous)
                  }
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-bold text-sky-700 transition hover:bg-sky-50 hover:text-sky-800 dark:text-sky-300 dark:hover:bg-sky-500/10 dark:hover:text-sky-200"
                >
                  {descriptionExpanded ? (
                    <>
                      Réduire
                      <FiChevronUp />
                    </>
                  ) : (
                    <>
                      Lire la suite
                      <FiChevronDown />
                    </>
                  )}
                </button>
              ) : null}
            </>
          ) : (
            <p className="text-sm italic leading-6 text-slate-400 dark:text-slate-500">
              Aucune description renseignée.
            </p>
          )}
        </div>

        {/* ------------------------------------------------------------------ */}
        {/*                         Informations rapides                       */}
        {/* ------------------------------------------------------------------ */}

        <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
          {item.tel_asso ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
              <FiPhone />
              {item.tel_asso}
            </span>
          ) : null}

          {item.mail_asso ? (
            <a
              href={`mailto:${item.mail_asso}`}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 transition hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
            >
              <FiMail />
              {item.mail_asso}
            </a>
          ) : null}

          {item.website_asso ? (
            <a
              href={normalizeExternalUrl(item.website_asso)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-3 py-1.5 text-sky-700 transition hover:bg-sky-100 dark:bg-sky-500/10 dark:text-sky-300"
            >
              <FiExternalLink />
              Site web
            </a>
          ) : null}

          {mapsUrl ? (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1.5 text-amber-700 transition hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300"
            >
              <FiMapPin />
              Carte
            </a>
          ) : null}
        </div>

        {/* ------------------------------------------------------------------ */}
        {/*                              Date                                  */}
        {/* ------------------------------------------------------------------ */}

        <p className="mt-4 text-xs font-bold text-slate-400 dark:text-slate-500">
          Créée le {formatDateTime(item.created_at)}
        </p>

        {/* ------------------------------------------------------------------ */}
        {/*                              Actions                               */}
        {/* ------------------------------------------------------------------ */}

        <div className="mt-5 flex flex-wrap gap-2">
          <AdminButton
            icon={FiEye}
            variant="secondary"
            size="sm"
            onClick={() => onPreview(item.id)}
          >
            Voir
          </AdminButton>

          <AdminButton
            icon={FiEdit2}
            variant="soft"
            size="sm"
            onClick={() => onEdit(item.id)}
          >
            Modifier
          </AdminButton>

          <AdminButton
            icon={item.is_active ? FiToggleLeft : FiToggleRight}
            variant={item.is_active ? "secondary" : "primary"}
            size="sm"
            loading={toggleLoading}
            onClick={() => onToggle(item.id)}
          >
            {item.is_active ? "Masquer" : "Publier"}
          </AdminButton>

          <AdminButton
            icon={FiTrash2}
            variant="dangerSoft"
            size="sm"
            onClick={() => onAskDelete(item.id)}
          >
            Supprimer
          </AdminButton>
        </div>
      </div>
    </article>
  );
}